import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import DynamicForm from '../components/DynamicForm';
import InlinePaymentSection, { availablePaymentMethods } from '../components/InlinePaymentSection';
import CashfreeCheckoutCard, { pendingRegistrationKey } from '../components/CashfreeCheckoutCard';
import DynamicBadgePrintModal from '../components/DynamicBadgePrintModal';
import { generateQrBase64 } from '../utils/qrCode';
import PrintingInProgress from '../components/PrintingInProgress';
import { deskPath, badgeShareAllowed } from '../utils/printWindow';
import useDeskPrintReturn from '../hooks/useDeskPrintReturn';
import { ApiService } from '../utils/api';
import { formatDate } from '../utils/datetime';
import { useToast } from '../components/Toast';
import { CheckCircle, AlertCircle, Clock, Users, Calendar, CreditCard } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { PAYMENT_COLLECTION_MODES } from '../utils/cashfree';
import { addPricedOption, chargeable, optionCurrency } from '../utils/pricing';
import { usePageTab } from '../utils/pageTab';
import { TicketingFooter } from '../components/ticketing/TicketingShell';
import { MAX_GROUP_SIZE, findNameField, parseNames, resizeNames } from '../utils/groupRegistration';
import { printBadgeBatch } from '../utils/badgePrint';
import { isMobilePrint } from '../utils/printWindow';
import { useEventRoute } from '../utils/eventRef';
import OfflineScanStatus from '../components/OfflineScanStatus';

// Kept for this tab only, so a refresh of the thank-you page still offers "Manage my registration".
const manageTokenKey = (eventId, userId) => `manage_token_${eventId}_${userId}`;

const rememberManageToken = (eventId, result) => {
  if (!result?.userId || !result?.manageToken) return;
  try {
    sessionStorage.setItem(manageTokenKey(eventId, result.userId), result.manageToken);
  } catch (_) {}
};

const EventRegistration = () => {
  const { eventId } = useEventRoute();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [formData, setFormData] = useState(null);
  const [showPaymentInstructions, setShowPaymentInstructions] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const paymentDraftRef = useRef(null);
  const [badgeUser, setBadgeUser] = useState(null);
  const [isPreparingBadge, setIsPreparingBadge] = useState(false);
  // On-spot group registration: person 1 fills the form, people 2..N only need a name.
  const [groupSize, setGroupSize] = useState(1);
  const [groupNames, setGroupNames] = useState([]);
  const [groupResult, setGroupResult] = useState(null);
  const [isPrintingAll, setIsPrintingAll] = useState(false);
  const handlePaymentDraftChange = useCallback((draft) => {
    paymentDraftRef.current = draft;
  }, []);

  // Helper to determine if this is an on-spot registration flow
  // Supports both:
  // 1) /register/:eventId/on-spot?enableCta=true
  // 2) /register/:eventId?source=ON_SPOT
  const isOnSpotRegistrationPath = () => {
    if (typeof window === 'undefined') return false;

    const pathname = window.location.pathname || '';
    console.log('pathname', pathname);
    const isOnSpotPath = pathname.endsWith('/on-spot');
    return isOnSpotPath;
  };

  const isOnSpotRegistrationWithSource = () => {
    if (typeof window === 'undefined') return false;

    const sourceParam = searchParams.get('source');
    const isOnSpotSource =
      typeof sourceParam === 'string' && (sourceParam.toUpperCase() === 'ON_SPOT' || sourceParam.toUpperCase() === 'ON-SPOT');

    return isOnSpotSource;
  };

  const isOnSpotRegistration = isOnSpotRegistrationPath() || isOnSpotRegistrationWithSource();

  console.log('isOnSpotRegistration', isOnSpotRegistration);

  const source = isOnSpotRegistration ? 'ON-SPOT' : (searchParams.get('source') || 'ONLINE');
  const enableCta = searchParams.get('enableCta') === 'true';
  const registrationIdParam = searchParams.get('registrationId');
  const registrationOutcomeParam = (searchParams.get('outcome') || '').toLowerCase();

  const PAYMENT_STORAGE_KEY = (id) => `registration_pending_${id}`;

  // Fetch event data (must be before any hooks that use event)
  const { data: event, isLoading, error } = useQuery({
    queryKey: ['publicEvent', eventId],
    queryFn: () => ApiService.getPublicEvent(eventId),
    enabled: !!eventId,
    retry: false, // Don't retry on auth errors
  });
  usePageTab({ title: event?.pageTitle || event?.name, icon: event?.faviconUrl });

  const { isPrintingBadge, handleBadgePrintStarted, backToDesk } = useDeskPrintReturn({
    fromDesk: searchParams.get('from') === 'desk',
    eventId,
    showProgress: event?.showPrintProgress !== false,
  });

  // Fetch event-specific payment settings (only for paid events)
  const { data: paymentSettingsResponse, error: paymentSettingsError } = useQuery({
    queryKey: ['publicEventPaymentSettings', eventId],
    queryFn: () => ApiService.getPublicEventPaymentSettings(eventId),
    enabled: !!event && event.eventType === 'paid',
    retry: false, // Don't retry on auth errors
  });

  // ONLINE vs OFFLINE — defaults to OFFLINE if config endpoint missing
  const { data: paymentConfig } = useQuery({
    queryKey: ['publicEventPaymentConfig', eventId],
    queryFn: () => ApiService.getPublicEventPaymentConfig(eventId),
    enabled: !!event && (event.eventType === 'paid' || event.eventType === 'PAID'),
    retry: false,
  });

  const paymentCollectionMode = (paymentConfig?.config?.mode || PAYMENT_COLLECTION_MODES.OFFLINE).toUpperCase();
  const [checkoutNotice, setCheckoutNotice] = useState(null);

  // Institute invite links: /register/:eventId?invite=CODE
  const inviteCode = searchParams.get('invite');
  const { data: invite } = useQuery({
    queryKey: ['publicInvite', inviteCode],
    queryFn: () => ApiService.getPublicInvite(inviteCode),
    enabled: !!inviteCode,
    retry: false,
  });
  const validInvite = invite?.valid && invite.eventId === eventId ? invite : null;
  const regDraftKey = isOnSpotRegistration ? null : `regDraft:${eventId}:${inviteCode || 'open'}`;

  useEffect(() => {
    if (!registrationComplete || !regDraftKey) return;
    try { localStorage.removeItem(regDraftKey); } catch (_) { /* storage unavailable */ }
  }, [registrationComplete, regDraftKey]);

  // Access codes inside an invite link (?access=CODE or typed): may make registration free or discounted.
  const [accessInput, setAccessInput] = useState(() => searchParams.get('access') || '');
  const [access, setAccess] = useState(null);
  const [accessError, setAccessError] = useState('');
  const [accessChecking, setAccessChecking] = useState(false);
  const applyAccessCode = useCallback(async (raw) => {
    const code = (raw || '').trim().toUpperCase().replace(/\s+/g, '');
    if (!code || !inviteCode) return;
    setAccessChecking(true);
    setAccessError('');
    try {
      const quote = await ApiService.getInviteAccessQuote(inviteCode, code, {});
      if (quote?.valid) {
        setAccess({ ...quote, code });
      } else {
        setAccess(null);
        setAccessError(quote?.reason || 'This access code is not valid.');
      }
    } catch (err) {
      setAccess(null);
      setAccessError(err?.message || 'Could not check the access code.');
    } finally {
      setAccessChecking(false);
    }
  }, [inviteCode]);
  const accessParam = searchParams.get('access');
  useEffect(() => {
    if (validInvite?.hasAccessCodes && accessParam) applyAccessCode(accessParam);
  }, [validInvite?.hasAccessCodes, accessParam, applyAccessCode]);
  const accessPriceRule = access?.benefit === 'COUPON' && access.discountType
    ? { discountType: access.discountType, discountValue: access.discountValue, maxDiscountAmount: access.maxDiscountAmount,
      label: `Access code ${access.code}${access.description ? ` (${access.description})` : ''}` }
    : null;
  const accessRequired = !!validInvite?.requireAccessCode && !access;
  const invitedFree = !!validInvite?.waivePayment || access?.benefit === 'FREE';

  const isOnlinePayment = paymentCollectionMode === PAYMENT_COLLECTION_MODES.ONLINE;

  // If URL already has a registrationId (e.g. after redirect), show thank-you on reload
  useEffect(() => {
    if (registrationIdParam && !registrationComplete) {
      let manageToken = null;
      try {
        manageToken = sessionStorage.getItem(manageTokenKey(eventId, registrationIdParam));
      } catch (_) {}
      setRegistrationData({ userId: registrationIdParam, manageToken });
      setRegistrationComplete(true);
    }
  }, [registrationIdParam, registrationComplete]);

  // Back from Cashfree's redirect flow (UPI apps, 3-D Secure): the order id is in the URL.
  const returnedOrderId = searchParams.get('order_id');
  useEffect(() => {
    if (!eventId || !returnedOrderId || registrationComplete) return;
    let cancelled = false;
    ApiService.verifyCashfreeOrderPublic(eventId, returnedOrderId)
      .then((verify) => {
        if (cancelled) return;
        if (verify.orderStatus === 'PAID') {
          handleOnlinePaid(verify);
        } else {
          setCheckoutNotice(`Your last payment attempt was not completed (${verify.orderStatus}). You can try again.`);
        }
      })
      .catch(() => !cancelled && setCheckoutNotice('We could not confirm your last payment yet. If money was deducted, check again in a minute.'));
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [eventId, returnedOrderId]);

  // Restore form data from sessionStorage on refresh when on payment step
  useEffect(() => {
    if (!eventId || !event) return;
    const step = searchParams.get('step');
    if (step !== 'payment') return;
    try {
      const stored = sessionStorage.getItem(PAYMENT_STORAGE_KEY(eventId));
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.formData && parsed.totalPrice == null) {
          parsed.totalPrice = calculateTotalPrice(parsed.formData);
        }
        setFormData(parsed);
        setShowPaymentInstructions(true);
      }
    } catch (e) {
      console.warn('Failed to restore payment step data', e);
    }
  }, [eventId, event, searchParams]);

  // On-spot can use its own page background; the online registration page keeps the form background.
  const formBackground = (isOnSpotRegistration && event?.pageBackgrounds?.ON_SPOT) || event?.formBackgroundUrl;

  const registrationFormSchema = isOnSpotRegistration && event?.onSpotFormSchema?.length
    ? event.onSpotFormSchema
    : event?.formSchema;

  // Calculate total price from form data
  function calculateTotalPrice(formData) {
    if (!registrationFormSchema) return 0;
    const normalize = (value) => (value || '').toString().trim().toLowerCase();

    const getFormValueIgnoreCase = (data, fieldLabel) => {
      const expected = normalize(fieldLabel);
      const key = Object.keys(data || {}).find((k) => normalize(k) === expected);
      return key ? data[key] : undefined;
    };

    const compute = (fields, totals = {}) => {
      (fields || []).forEach((field) => {
        if (field?.type === 'list' && field.price > 0) {
          const listValue = getFormValueIgnoreCase(formData, field.label);
          if (Array.isArray(listValue)) {
            const currency = optionCurrency(field);
            totals[currency] = (totals[currency] || 0) + field.price * listValue.length;
          }
          return;
        }

        if (!field?.optionsWithPricing?.length) return;
        const selectedValue = getFormValueIgnoreCase(formData, field.label);
        if (selectedValue == null) return;

        const selectedOption = field.optionsWithPricing.find(
          (opt) => normalize(opt?.label) === normalize(selectedValue)
        );
        if (!selectedOption) return;

        Object.assign(totals, addPricedOption(totals, selectedOption));

        if (selectedOption?.subFields?.length) {
          compute(selectedOption.subFields, totals);
        }
      });
      return totals;
    };

    return chargeable(compute(registrationFormSchema), event?.currency || 'INR');
  }

  // Extract payment settings from response (should be a single object, not array)
  let paymentSettings = null;

  if (paymentSettingsResponse && !Array.isArray(paymentSettingsResponse)) {
    // Response is a single PaymentSettingsDto object
    paymentSettings = paymentSettingsResponse;
  } else if (Array.isArray(paymentSettingsResponse) && paymentSettingsResponse.length > 0) {
    // Fallback: handle array response (legacy)
    paymentSettings = paymentSettingsResponse[0];
  }

  if (!paymentSettings && event?.paymentSettings) {
    paymentSettings = event.paymentSettings;
  }

  const offlinePaymentMethods = availablePaymentMethods(paymentSettings);

  const validatePayment = () => {
    if (isOnlinePayment || offlinePaymentMethods.length === 0) return null;
    const draft = paymentDraftRef.current || {};
    if (!draft.paymentMethod) return 'Please select a payment method.';
    if (!draft.transactionId) return 'Please enter the transaction ID / UTR of your payment.';
    if (!draft.documentId) return 'Please upload the payment screenshot.';
    return null;
  };

  // Handle form submission
  const handleRegistration = async (formData, documentIds = [], userId = null, faceImage = null, couponQuote = null) => {
    const computedPrice = calculateTotalPrice(formData);
    const totalPrice = couponQuote?.finalAmount != null ? Number(couponQuote.finalAmount) : computedPrice;
    const payload = {
      formData, documentIds, userId, faceImage, totalPrice, couponHoldToken: couponQuote?.holdToken, couponQuote,
      inviteCode: validInvite ? inviteCode : undefined,
      accessCode: validInvite && access ? access.code : undefined,
    };
    setFormData(payload);

    if (isOnSpotRegistration && groupSize > 1) {
      await submitGroup(formData, documentIds, faceImage, couponQuote, totalPrice);
      return;
    }

    if (invitedFree) {
      try {
        await submitRegistration(formData, null, documentIds, userId, faceImage, null, 0);
      } catch (_) {
        // submitRegistration already showed the error
      }
      return;
    }

    const isPaidEvent = event.eventType === 'paid' || event.eventType === 'PAID';

    // Offline payment proof is collected inside the form, so register in one step.
    if (isPaidEvent && totalPrice > 0 && !isOnlinePayment) {
      const draft = paymentDraftRef.current || {};
      const paymentData = draft.paymentMethod
        ? {
          paymentMethod: draft.paymentMethod,
          transactionId: draft.transactionId,
          amount: totalPrice,
          ...(draft.remark ? { remark: draft.remark } : {}),
        }
        : null;
      const allDocumentIds = [...(documentIds || []), draft.documentId].filter(Boolean);
      try {
        await submitRegistration(formData, paymentData, allDocumentIds, userId, faceImage, couponQuote?.holdToken, totalPrice);
      } catch (_) {
        // submitRegistration already showed the error
      }
      return;
    }

    // Online gateway checkout runs on its own step so refresh preserves data
    if (isPaidEvent && totalPrice > 0) {
      try {
        sessionStorage.setItem(PAYMENT_STORAGE_KEY(eventId), JSON.stringify(payload));
        const params = new URLSearchParams(window.location.search);
        params.set('step', 'payment');
        navigate({ pathname: window.location.pathname, search: params.toString() }, { replace: true });
      } catch (e) {
        console.warn('Failed to persist payment step', e);
      }
      setShowPaymentInstructions(true);
      return;
    }

    // For free events or paid events with free categories, proceed with registration
    await submitRegistration(formData, null, documentIds, userId, faceImage, couponQuote?.holdToken, totalPrice);
  };

  const submitGroup = async (formValues, documentIds, faceImage, couponQuote, pricePerPerson) => {
    const nameField = findNameField(registrationFormSchema);
    const names = [formValues?.[nameField?.label], ...groupNames].map((n) => (n || '').trim());
    if (!nameField || names.some((n) => !n)) {
      showError(`Enter a name for all ${groupSize} people.`);
      return;
    }
    const isPaidEvent = event.eventType === 'paid' || event.eventType === 'PAID';
    const charged = isPaidEvent && pricePerPerson > 0 && !invitedFree;
    if (charged && offlinePaymentMethods.length > 0) {
      const paymentError = validatePayment();
      if (paymentError) {
        showError(paymentError);
        return;
      }
    }
    const draft = paymentDraftRef.current || {};
    setIsSubmitting(true);
    try {
      const result = await ApiService.registerGroup(eventId, {
        nameField: nameField.label,
        names,
        request: {
          formData: formValues,
          source,
          paymentStatus: charged ? 'pending' : 'free',
          paymentData: charged && draft.paymentMethod ? {
            paymentMethod: draft.paymentMethod,
            transactionId: draft.transactionId,
            amount: pricePerPerson,
            groupTotal: pricePerPerson * names.length,
            ...(draft.remark ? { remark: draft.remark } : {}),
          } : null,
          documentIds: [...(documentIds || []), draft.documentId].filter(Boolean),
          faceImageBase64: faceImage,
          couponHoldToken: couponQuote?.holdToken,
          inviteCode: validInvite ? inviteCode : undefined,
          accessCode: validInvite && access ? access.code : undefined,
        },
      });
      const people = (result.registered || []).map((r, i) => ({ userId: r.userId, status: r.status, name: result.names?.[i] }));
      if (!people.length) {
        showError(result.failed?.[0]?.error || 'No registrations were created.');
        return;
      }
      setGroupResult({ people, failed: result.failed || [], formValues, nameField: nameField.label });
      setRegistrationData({ userId: people[0].userId, status: people[0].status });
      setRegistrationComplete(true);
      if (result.failed?.length) {
        showError(`${result.failed.length} of ${names.length} could not be registered.`);
      } else {
        showSuccess(`${people.length} registrations created.`);
      }
    } catch (err) {
      showError(err?.message || 'Group registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const printAllBadges = async () => {
    if (!groupResult) return;
    // Opened inside the click so popup blockers allow it; phones print from this page instead.
    const printWindow = isMobilePrint() ? null : window.open('', '_blank', 'width=800,height=600');
    setIsPrintingAll(true);
    try {
      const config = await ApiService.getBadgeDesignerConfig(eventId);
      if (!config?.paperSize) throw new Error('No badge design saved for this event');
      const users = await Promise.all(groupResult.people.map(async (person) => ({
        ...groupResult.formValues,
        [groupResult.nameField]: person.name,
        userId: person.userId,
        qrCodeBase64: await generateQrBase64(person.userId),
      })));
      const handle = printBadgeBatch(config, users, printWindow);
      handleBadgePrintStarted?.(handle);
    } catch (err) {
      printWindow?.close();
      showError(err?.message || 'Could not prepare the badges');
    } finally {
      setIsPrintingAll(false);
    }
  };

  // Submit registration with payment data
  const submitRegistration = async (formData, paymentData = null, documentIds = [], userId = null, faceImage = null, couponHoldToken = null, payableAmount = null) => {
    setIsSubmitting(true);
    try {
      const isPaidEvent = event.eventType === 'paid' || event.eventType === 'PAID';
      const totalPrice = payableAmount != null ? Number(payableAmount) : calculateTotalPrice(formData);
      const registrationPayload = {
        formData,
        source: source, // Include source parameter
        paymentStatus: isPaidEvent && (payableAmount == null || payableAmount > 0) ? 'pending' : 'free',
        documentIds: documentIds, // Include uploaded document IDs
        userId: userId, // Include userId from file upload response
        faceImageBase64: faceImage // Include face image for registration
      };

      if (paymentData) {
        registrationPayload.paymentData = paymentData;
      }
      if (couponHoldToken) {
        registrationPayload.couponHoldToken = couponHoldToken;
      }
      if (validInvite) {
        registrationPayload.inviteCode = inviteCode;
        if (access) registrationPayload.accessCode = access.code;
      }

      const result = isOnSpotRegistration
        ? await ApiService.registerOnSpot(eventId, registrationPayload)
        : await ApiService.registerUserPublic(eventId, registrationPayload);
      const autoApproved = isPaidEvent && totalPrice > 0 && result?.status === 'REGISTERED';
      setRegistrationData(result);
      rememberManageToken(eventId, result);
      setRegistrationComplete(true);

      // Clear persisted payment step data
      try {
        sessionStorage.removeItem(PAYMENT_STORAGE_KEY(eventId));
      } catch (_) {}

      // Update URL with registrationId so reload keeps thank-you page
      if (result?.userId) {
        try {
          const currentParams = new URLSearchParams(window.location.search);
          currentParams.delete('step'); // Remove payment step param
          const outcome = isPaidEvent
            ? (totalPrice > 0 ? (autoApproved ? 'approved' : 'pending') : 'free')
            : 'complete';
          currentParams.set('registrationId', result.userId);
          currentParams.set('outcome', outcome);
          navigate(
            {
              pathname: window.location.pathname,
              search: currentParams.toString(),
            },
            { replace: true }
          );
        } catch (err) {
          console.warn('Failed to update URL after registration', err);
        }
      }

      console.log('EventRegistration - result after submitting :', result);

      if (result?.offline) {
        showSuccess(`Saved on this device (ID ${result.userId}). It uploads automatically when the connection is back.`);
      } else if (autoApproved) {
        showSuccess('Registration confirmed! Check your email for details.');
      } else if (isPaidEvent && totalPrice > 0) {
        showSuccess('Registration submitted! Payment approval is pending.');
      } else if (invitedFree) {
        showSuccess(`Registration confirmed! You're registered as a guest of ${validInvite.name}.`);
      } else if (isPaidEvent && totalPrice === 0) {
        showSuccess('Registration successful! You selected a free category.');
      } else {
        showSuccess(`Registration successful! with Registeration id ${result?.userId} Check your email for confirmation.`);
      }

      return result; // Return the result for payment proof upload
    } catch (error) {
      console.error('Registration error:', error);
      showError(error?.message || 'Registration failed. Please try again.');
      throw error; // Re-throw error for payment proof handling
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Cashfree online payment success (backend already marked PAID)
  const handleOnlinePaid = async (verifyResult) => {
    try {
      sessionStorage.removeItem(PAYMENT_STORAGE_KEY(eventId));
      sessionStorage.removeItem(pendingRegistrationKey(eventId));
    } catch (_) {}

    const userId = verifyResult?.userId || verifyResult?.registrationId;
    const confirmed = verifyResult?.registrationStatus === 'REGISTERED';
    setRegistrationData({
      userId,
      orderId: verifyResult?.orderId,
      status: verifyResult?.registrationStatus,
      paymentStatus: confirmed ? 'paid' : undefined,
      manageToken: verifyResult?.manageToken,
    });
    rememberManageToken(eventId, { userId, manageToken: verifyResult?.manageToken });
    setRegistrationComplete(true);

    if (userId) {
      try {
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.delete('step');
        currentParams.set('registrationId', userId);
        currentParams.set('outcome', confirmed ? 'paid' : 'pending');
        navigate(
          { pathname: window.location.pathname, search: currentParams.toString() },
          { replace: true }
        );
      } catch (err) {
        console.warn('Failed to update URL after online payment', err);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <img
          src="/logo.png"
          alt="EventEase"
          className="h-16 w-auto mb-8"
        />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <img
          src="/logo.png"
          alt="EventEase"
          className="h-16 w-auto mb-8"
        />
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Event Not Found</h2>
            <p className="text-gray-600 mb-4">
              The event you're looking for doesn't exist or has been removed.
            </p>
            <Button onClick={() => navigate('/')}>
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Resolve logo: form logo overrides event logo. If formLogoUrl is set, use it; else eventLogo; else default.
  const resolvedLogo =
    (event?.formLogoUrl && !logoError)
      ? event.formLogoUrl
      : (event?.eventLogo && !logoError)
        ? event.eventLogo
        : '/logo.png';
  const showFormLogo = event?.showFormLogo !== false;

  const isOnSpotDesk = (source === 'ON-SPOT' || source === 'ON_SPOT') && enableCta;
  // Opened from the event desk: after the badge (or straight away) go back to the desk.
  const fromDesk = searchParams.get('from') === 'desk';


  const openBadgePrint = async () => {
    const userId = registrationData?.userId;
    if (!userId) return;
    setIsPreparingBadge(true);
    try {
      const registration = await ApiService.getRegistration(eventId, userId);
      setBadgeUser({ ...registration, userId: registration?.userId || userId });
    } catch (err) {
      // Desk without access to the registration API: fall back to what was just submitted.
      console.warn('Falling back to submitted data for badge print', err);
      setBadgeUser({
        userId,
        formData: formData?.formData || {},
        qrCodeBase64: await generateQrBase64(userId),
      });
    } finally {
      setIsPreparingBadge(false);
    }
  };

  if (registrationComplete) {
    return (
      <div
        className={`min-h-screen flex flex-col items-center justify-center p-4 ${!formBackground ? 'bg-gray-50' : ''}`}
        style={formBackground ? {
          backgroundImage: `url(${formBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        } : undefined}
      >
        {showFormLogo && (
          <img
            src={resolvedLogo}
            alt="Event Logo"
            className="w-full max-w-2xl mx-auto h-auto max-h-48 object-contain object-center mb-8 rounded-lg"
            onError={() => setLogoError(true)}
          />
        )}
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Registration Complete!
            </h2>
            {groupResult ? (
              <div className="mb-6 text-left">
                <p className="text-gray-600 mb-2 text-center">{groupResult.people.length} people registered</p>
                <ul className="max-h-64 overflow-y-auto divide-y rounded border text-sm">
                  {groupResult.people.map((person) => (
                    <li key={person.userId} className="flex justify-between gap-2 px-3 py-1.5">
                      <span className="truncate">{person.name}</span>
                      <span className="font-mono text-gray-500 shrink-0">{person.userId}</span>
                    </li>
                  ))}
                </ul>
                {groupResult.failed.length > 0 && (
                  <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                    Not registered: {groupResult.failed.map((f) => `${f.name} (${f.error})`).join('; ')}
                  </div>
                )}
              </div>
            ) : isOnSpotDesk ? (
              <div className="mb-6 space-y-3">
                <p className="text-gray-600">
                  Registration ID <strong className="font-mono">{registrationData?.userId}</strong>
                </p>
                {registrationData?.offline && (
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    Saved on this device while offline. Print the badge now; the registration uploads automatically when the connection is back.
                  </p>
                )}
                {event?.offlineEnabled && <OfflineScanStatus eventId={eventId} offlineEnabled />}
              </div>
            ) : event.eventType === 'paid' ? (
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  Thank you for registering for <strong>{event.name}</strong>.
                </p>
                {(() => {
                  // Resolve outcome robustly across refresh:
                  // - immediate post-submit: derive from formData
                  // - after reload: derive from URL outcome param
                  const totalPrice = formData?.totalPrice ?? (formData?.formData ? calculateTotalPrice(formData.formData) : null);
                  const isPaidOnline =
                    registrationOutcomeParam === 'paid' ||
                    registrationData?.paymentStatus === 'paid';
                  const isAutoApproved =
                    registrationOutcomeParam === 'approved' ||
                    ((formData?.totalPrice ?? 0) > 0 && registrationData?.status === 'REGISTERED');
                  const isPendingPayment = isPaidOnline
                    ? false
                    : totalPrice != null
                      ? totalPrice > 0
                      : (registrationOutcomeParam ? registrationOutcomeParam === 'pending' : true);

                  if (isPaidOnline) {
                    return (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <CreditCard className="w-5 h-5 text-green-600" />
                          <h3 className="font-medium text-green-800">Payment received</h3>
                        </div>
                        <p className="text-sm text-green-700">
                          Your online payment was confirmed. Check your email for registration details.
                        </p>
                      </div>
                    );
                  }

                  if (isAutoApproved) {
                    return (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <h3 className="font-medium text-green-800">Registration confirmed</h3>
                        </div>
                        <p className="text-sm text-green-700">
                          We received your payment details. Your registration ID is{' '}
                          <strong>{registrationData?.userId}</strong> — check your email for the confirmation.
                        </p>
                      </div>
                    );
                  }

                  if (isPendingPayment) {
                    return (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <Clock className="w-5 h-5 text-yellow-600" />
                          <h3 className="font-medium text-yellow-800">Payment Pending</h3>
                        </div>
                        <p className="text-sm text-yellow-700">
                          Your registration is pending payment approval. You will receive an email confirmation
                          once your payment is verified by our team.
                        </p>
                      </div>
                    );
                  } else {
                    return (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <h3 className="font-medium text-green-800">Free Category Selected</h3>
                        </div>
                        <p className="text-sm text-green-700">
                          You selected a free category. Your registration is complete and you should receive
                          a confirmation email shortly.
                        </p>
                      </div>
                    );
                  }
                })()}
              </div>
            ) : (
              <>
                <p className="text-gray-600 mb-6">
                  Thank you for registering for <strong>{event.name}</strong> with Registration id{' '}
                  <strong>{registrationData?.userId}</strong>. You should receive a confirmation email shortly.
                </p>

                {/* QR Code based on registration ID so user can screenshot */}
                {registrationData?.userId && (
                  <div className="mb-6">
                    <p className="text-sm text-gray-600 mb-2">
                      Your QR Code (based on Registration ID {registrationData.userId}):
                    </p>
                    <div className="bg-white p-4 rounded-lg border-2 border-dashed border-gray-300 inline-flex items-center justify-center">
                      <QRCodeSVG value={registrationData.userId} size={160} />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      You can take a screenshot of this QR code and use it for event check-in or badge printing.
                    </p>
                  </div>
                )}

              </>
            )}
            {/*             
            {registrationData?.qrCodeUrl && (
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-2">Your QR Code:</p>
                <div className="bg-white p-4 rounded-lg border-2 border-dashed border-gray-300">
                  <img 
                    src={registrationData.qrCodeUrl} 
                    alt="QR Code" 
                    className="w-32 h-32 mx-auto"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Save this QR code for event check-in
                </p>
              </div>
            )} */}

            <div className="space-y-2">
              {!isOnSpotDesk && !groupResult && event.selfServiceEnabled && (
                <Button
                  variant={registrationData?.manageToken ? 'default' : 'outline'}
                  className="w-full"
                  onClick={() => navigate(registrationData?.manageToken
                    ? `/my-registration/${registrationData.manageToken}`
                    : `/my-registration?event=${eventId}`)}
                >
                  Manage my registration
                </Button>
              )}
              {isOnSpotDesk && (
                <>
                  {groupResult ? (
                    <Button onClick={printAllBadges} disabled={isPrintingAll} className="w-full">
                      {isPrintingAll ? 'Preparing badges…' : `🖨️ Print all ${groupResult.people.length} badges`}
                    </Button>
                  ) : registrationData?.userId && (
                    <Button onClick={openBadgePrint} disabled={isPreparingBadge} className="w-full">
                      {isPreparingBadge ? 'Preparing badge…' : '🖨️ Print Badge'}
                    </Button>
                  )}
                  {fromDesk ? (
                    <Button variant="outline" onClick={() => navigate(deskPath(eventId))} className="w-full">
                      Back to desk
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setBadgeUser(null);
                        setGroupResult(null);
                        setGroupSize(1);
                        setGroupNames([]);
                        setRegistrationComplete(false);
                        setRegistrationData(null);
                        setFormData(null);
                        // Without this the previous person's payment step stays open instead of a blank form.
                        setShowPaymentInstructions(false);
                        try {
                          sessionStorage.removeItem(PAYMENT_STORAGE_KEY(eventId));
                        } catch (_) {}
                        // Clean registrationId from URL when starting a new registration
                        try {
                          const params = new URLSearchParams(window.location.search);
                          params.delete('registrationId');
                          params.delete('outcome');
                          params.delete('step');
                          navigate(
                            {
                              pathname: window.location.pathname,
                              search: params.toString(),
                            },
                            { replace: true }
                          );
                        } catch (err) {
                          console.warn('Failed to clear registrationId from URL', err);
                        }
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full"
                    >
                      Register Another Person
                    </Button>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
        {badgeUser && (
          <DynamicBadgePrintModal
            isOpen
            onClose={() => setBadgeUser(null)}
            eventId={eventId}
            scannedUser={badgeUser}
            onPrintStarted={handleBadgePrintStarted}
            shareEnabled={badgeShareAllowed(event)}
          />
        )}
        {isPrintingBadge && <PrintingInProgress onDone={backToDesk} />}
      </div>
    );
  }

  // Check if event is at capacity
  // const isAtCapacity = event.eventType === 'limited' && event.registered >= event.capacity;
  const isAtCapacity = isOnSpotRegistration
    ? false
    : event.capacity != null && (event.registered ?? 0) >= event.capacity;
  const closedByAdmin = isOnSpotRegistration ? !!event.onSpotRegistrationsClosed : !!event.registrationsClosed;
  const isWaitlistEnabled = !!event.waitlistEnabled && isAtCapacity && !closedByAdmin;
  const isClosed = closedByAdmin || (isAtCapacity && !isWaitlistEnabled);
  const closedMessage = (isOnSpotRegistration ? event.onSpotClosedMessage : event.registrationClosedMessage)
    || (isOnSpotRegistration
      ? 'On-spot registrations are closed for this event.'
      : 'This event has reached its capacity limit and is no longer accepting registrations.');

  return (
    <div
      className={`min-h-screen py-6 sm:py-8 ${!formBackground ? 'bg-gradient-to-br from-blue-50 to-indigo-50' : ''}`}
      style={formBackground ? {
        backgroundImage: `url(${formBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      } : undefined}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Logo - full width, fit entire image without cropping */}
        {showFormLogo && (
          <div className="w-full mb-4 rounded-lg">
            <img
              src={resolvedLogo}
              alt="Event Logo"
              className="w-full h-auto max-h-60 object-contain object-center drop-shadow-md"
              onError={() => setLogoError(true)}
            />
          </div>
        )}
        {/* Event Header */}
        <Card className="mb-4 sm:mb-6">
          <CardHeader className="pb-3 sm:pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-xl sm:text-2xl">{event.name}</CardTitle>
                {event.metaData && (
                  <div
                    className="mt-2 text-sm text-gray-600 [&_p]:my-1 [&_h1]:text-base [&_h2]:text-sm [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-4 [&_ol]:pl-4"
                    dangerouslySetInnerHTML={{ __html: event.metaData }}
                  />
                )}
              </div>
              <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-500 shrink-0">
                <div className="flex items-center">
                  <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                  {event.eventEndDate
                    ? `${formatDate(event.eventDate)} – ${formatDate(event.eventEndDate)}`
                    : formatDate(event.eventDate)}
                </div>
              </div>
            </div>
          </CardHeader>
          {(isClosed || isWaitlistEnabled) && (
            <CardContent className="pt-0 pb-3">
              <div className="flex items-center justify-center">
                {isClosed && (
                  <div className="flex items-center text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    <span className="font-medium">{closedByAdmin ? 'Registrations Closed' : 'Event Full'}</span>
                  </div>
                )}

                {isWaitlistEnabled && (
                  <div className="flex items-center text-yellow-600 text-sm">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    <span className="font-medium">Waitlist Available</span>
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Registration Form */}
        <div className="relative">
          <div className="absolute inset-0 bg-white/40 backdrop-blur-sm rounded-2xl"></div>
          <div className="relative">
            {!isClosed ? (
              <>
                {!(showPaymentInstructions && isOnlinePayment) ? (
                  <div
                    className="rounded-2xl shadow-xl border border-gray-100 overflow-hidden relative"
                    style={{
                      backgroundColor: formBackground ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.8)',
                    }}
                  >
                    <div className="p-4 sm:p-5 border-b border-gray-100/80 relative z-10">
                      <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Registration Form</h2>
                      <p className="mt-1 text-sm text-gray-600">Fill in your details below</p>
                      {validInvite && (
                        <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                          You're invited by <b>{validInvite.name}</b>.
                          {invitedFree ? ' Registration is free for you.' : ''}
                          {!invitedFree && access?.benefit === 'COUPON' && access.description ? ` Your invitation includes ${access.description}.` : ''}
                          {validInvite.emailRestricted ? ' Use the email address your invitation was sent to.' : ''}
                        </div>
                      )}
                      {/* A code that came inside the link is applied silently; the box is only for typed codes. */}
                      {validInvite?.hasAccessCodes && !(accessParam && accessChecking) && !(accessParam && access && access.code === accessParam.trim().toUpperCase().replace(/\s+/g, '')) && (
                        <div className="mt-3 rounded-lg border border-gray-200 bg-white/80 p-3">
                          <label htmlFor="invite-access-code" className="block text-sm font-medium text-gray-800">
                            Access code{validInvite.requireAccessCode ? ' *' : ' (if you were given one)'}
                          </label>
                          {access ? (
                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">
                              <span>
                                <b className="font-mono">{access.code}</b>
                                {access.label ? ` · ${access.label}` : ''}
                                {access.benefit === 'FREE' ? ' · Registration is free' : ''}
                                {access.benefit === 'COUPON' && access.description ? ` · ${access.description}` : ''}
                              </span>
                              <button type="button" className="text-xs font-medium text-green-900 underline"
                                onClick={() => { setAccess(null); setAccessInput(''); }}>
                                Change
                              </button>
                            </div>
                          ) : (
                            <form
                              className="mt-2 flex gap-2"
                              onSubmit={(e) => { e.preventDefault(); applyAccessCode(accessInput); }}
                            >
                              <input
                                id="invite-access-code"
                                value={accessInput}
                                onChange={(e) => setAccessInput(e.target.value)}
                                autoCapitalize="characters"
                                autoComplete="off"
                                className="h-10 min-w-0 flex-1 rounded-md border border-gray-300 px-3 font-mono uppercase"
                                placeholder="Enter code"
                              />
                              <Button type="submit" disabled={accessChecking || !accessInput.trim()}>
                                {accessChecking ? 'Checking…' : 'Apply'}
                              </Button>
                            </form>
                          )}
                          {accessError && <p className="mt-2 text-sm text-red-600">{accessError}</p>}
                        </div>
                      )}
                      {event.inviteOnly && !validInvite && !isOnSpotRegistration && (
                        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                          Registration for this event is by invitation only. Use the invite link you were sent.
                        </div>
                      )}
                      {inviteCode && invite && !validInvite && (
                        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                          {invite.reason || 'This invite link is not valid for this event.'} You can still register normally below.
                        </div>
                      )}
                      {event.selfServiceEnabled && !isOnSpotRegistration && (
                        <a href={`/my-registration?event=${eventId}`} className="mt-1 inline-block text-sm text-blue-600 hover:underline">
                          Already registered? View or change your registration
                        </a>
                      )}
                    </div>
                    <div className="relative z-10">
                      {isOnSpotRegistration && event?.offlineEnabled && (
                        <div className="mx-4 mt-4 sm:mx-5">
                          <OfflineScanStatus eventId={eventId} offlineEnabled />
                        </div>
                      )}
                      {isOnSpotRegistration && !(isOnlinePayment && (event.eventType === 'paid' || event.eventType === 'PAID') && !invitedFree) && (() => {
                        const nameField = findNameField(registrationFormSchema);
                        if (!nameField) return null;
                        return (
                          <div className="mx-4 sm:mx-5 mt-4 rounded-lg border border-blue-100 bg-blue-50/60 p-3 text-sm">
                            <label className="flex flex-wrap items-center gap-2 font-medium text-gray-800">
                              Registering a group?
                              <input
                                type="number"
                                min="1"
                                max={MAX_GROUP_SIZE}
                                value={groupSize}
                                onChange={(e) => {
                                  const size = Math.max(1, Math.min(MAX_GROUP_SIZE, Number(e.target.value) || 1));
                                  setGroupSize(size);
                                  setGroupNames((prev) => resizeNames(prev, size));
                                }}
                                className="h-8 w-20 rounded border border-gray-300 px-2"
                              />
                              people
                            </label>
                            {groupSize > 1 && (
                              <div className="mt-3 space-y-2">
                                <p className="text-xs text-gray-600">
                                  Fill the form below for person 1. Everyone else gets the same details (email, phone, …) with their own
                                  “{nameField.label}”, registration ID and badge. Paid events charge each person the same amount.
                                </p>
                                <textarea
                                  rows={2}
                                  placeholder="Paste names here, one per line, to fill the list"
                                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                  onChange={(e) => {
                                    const pasted = parseNames(e.target.value);
                                    if (pasted.length) {
                                      const size = Math.min(MAX_GROUP_SIZE, Math.max(groupSize, pasted.length + 1));
                                      setGroupSize(size);
                                      setGroupNames(resizeNames(pasted, size));
                                    }
                                  }}
                                />
                                <div className="grid gap-2 sm:grid-cols-2">
                                  {groupNames.map((name, index) => (
                                    <input
                                      key={index}
                                      value={name}
                                      placeholder={`Person ${index + 2} – ${nameField.label}`}
                                      onChange={(e) => setGroupNames((prev) => prev.map((n, i) => (i === index ? e.target.value : n)))}
                                      className="h-9 rounded border border-gray-300 px-2"
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {accessRequired ? (
                        <p className="m-4 sm:m-5 rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
                          Enter the access code you were given to continue.
                        </p>
                      ) : (
                      <DynamicForm
                        formSchema={registrationFormSchema}
                        draftKey={regDraftKey}
                        priceRule={invitedFree ? null : accessPriceRule}
                        onSubmit={handleRegistration}
                        submitLabel={isWaitlistEnabled ? 'Join Waitlist' : (isOnSpotRegistration && groupSize > 1 ? `Register ${groupSize} people` : (isOnlinePayment && !invitedFree ? 'Continue to Payment' : 'Register Now'))}
                        isLoading={isSubmitting}
                        showPricing={(event.eventType === 'paid' || event.eventType === 'PAID') && !invitedFree}
                        eventId={eventId}
                        eventData={event}
                        hasFormBackground={!!formBackground}
                        renderPayment={isOnlinePayment || invitedFree ? null : (amount) => (
                          <InlinePaymentSection
                            amount={amount}
                            paymentSettings={paymentSettings}
                            eventId={eventId}
                            onChange={handlePaymentDraftChange}
                          />
                        )}
                        validatePayment={validatePayment}
                      />
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    className="rounded-2xl shadow-xl border border-gray-100"
                    style={{
                      backgroundColor: formBackground ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.8)',
                    }}
                  >
                    <CashfreeCheckoutCard
                        eventId={eventId}
                        eventName={event.name}
                        currency={event.currency}
                        formPayload={formData}
                        paymentConfig={paymentConfig?.config}
                        source={source}
                        notice={checkoutNotice}
                        onPaid={handleOnlinePaid}
                        onBack={() => {
                          try {
                            sessionStorage.removeItem(pendingRegistrationKey(eventId));
                          } catch (_) {}
                          setCheckoutNotice(null);
                          setShowPaymentInstructions(false);
                          const params = new URLSearchParams(window.location.search);
                          params.delete('step');
                          navigate(
                            { pathname: window.location.pathname, search: params.toString() },
                            { replace: true }
                          );
                        }}
                      />
                  </div>
                )}
              </>
            ) : (
              <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="h-8 w-8 text-red-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Registration Closed</h3>
                  <p className="text-gray-600 text-lg whitespace-pre-line">{closedMessage}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Event Details */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-lg">Event Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* <div>
              <h4 className="font-medium text-gray-900">Event Type</h4>
              <p className="text-gray-600 capitalize">{event.eventType}</p>
            </div> */}

            {/* <div>
              <h4 className="font-medium text-gray-900">Capacity</h4>
              <p className="text-gray-600">
                {event.registered} of {event.capacity} spots filled
                {event.waitlistEnabled && ' (Waitlist enabled)'}
              </p>
            </div> */}

            <div>
              <h4 className="font-medium text-gray-900">Registration Status</h4>
              <p className="text-gray-600">
                {isClosed ? 'Closed' : (isWaitlistEnabled ? 'Waitlist Only' : 'Open')}
              </p>
            </div>


            {event?.description && (
              <div>
                <h4 className="font-medium text-gray-900">About Event</h4>
                <div
                  className="text-gray-600 mt-1 text-sm"
                  dangerouslySetInnerHTML={{ __html: event.description }}
                />
              </div>
            )}
          </CardContent>
        </Card>
        <TicketingFooter show={event.showPoweredBy !== false} logos={event.poweredByLogos || []} />
      </div>
    </div>
  );
};

export default EventRegistration;
